import { MyleTableColumn } from "@/Common/Components/Tables/MyleTableColumn";
import { PortfolioInboxes, PortfolioNotesSortOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { BillingProvider } from "@/Common/Services/ExternalBilling/BillingProvider";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { NotesInboxTableItem, PreventiveRecallListTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class NotesInboxHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Portfolio.PortfolioTab_didMount, this.load_NotesInbox);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchBoxTextChanged, this.seacrh_TextChanged);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_Reload, this.Search_Reload);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_Search_CurrentPageChange, this.resultsPageChange);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchSettings_OnResultsPerPageChange, this.resulstPerPageChange);
        this.RegisterOperation(LocalOperations.Misc.Table_ColumnSortClick, this.setColumnSort);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_OnPatientClick, this.onPatientClick);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_Refresh_ClaimStatus, this.refreshClaimStatus);
        this.RegisterOperation(LocalOperations.Portfolio.PreventiveRecallList_OnGenerateLettersClick, this.onGenerateLettersClick);
    }

    private onGenerateLettersClick(request: LocalEvent, store: PortfolioStore): void {
        const letters = request.Get<Immutable.List<PreventiveRecallListTableItem>>("letters");
        store.PortfolioActions.generateRecallListLetters(letters);
    }

    private resultsPageChange(request: LocalEvent, store: PortfolioStore): void {
        let value = request.Get<number>("Page");
        store.NotesInboxModel = store.NotesInboxModel.set("SelectedPageNumber", value);
        store.PortfolioActions.LoadMyNotes(store.NotesInboxModel);
    }

    private load_NotesInbox(request: LocalEvent, store: PortfolioStore): void {
        if (!store.initialLoadDone) {
            store.LoadMyNotesSitesFromCache();
            store.LoadMyNotesClinicianUICache();
            store.initialLoadDone = true;

            // always reload statuses on mount (displayed values could have changed with language switch...)
            // DV 2018-03-11 - bring that linbe back into the "If ! InitLoad" as a quick fix for an issue where Checked statuswould always reset to default when doctor switch between patient tab and portofolio. Too bad for language for now.
            store.InitializeMyNotesStatus();
        }

        if (store.Model.get("CurrentInbox") == PortfolioInboxes.Notes) {
            store.PortfolioActions.LoadMyNotes(store.NotesInboxModel);
            store.PortfolioActions.GetApssCount();
            // store.PortfolioActions.GetFailedFaxesCount();
        }
    }

    private resulstPerPageChange(request: LocalEvent, store: PortfolioStore): void {
        store.NotesInboxModel = store.NotesInboxModel.set("SelectedPageNumber", 1);
        store.NotesInboxModel = store.NotesInboxModel.set("SelectedPageSizeOption", request.Get<number>("value"));
    }

    private Search_Reload(request: LocalEvent, store: PortfolioStore): void {
        store.NotesInboxModel = store.NotesInboxModel.set("SelectedPageNumber", 1);
        store.PortfolioActions.LoadMyNotes(store.NotesInboxModel);
    }

    private seacrh_TextChanged(request: LocalEvent, store: PortfolioStore): void {
        store.NotesInboxModel = store.NotesInboxModel.set("SelectedPageNumber", 1);
        store.NotesInboxModel = store.NotesInboxModel.set("GlobalPortfolioSearchText", request.Get<string>("value"));
        store.PortfolioActions.LoadMyNotes(store.NotesInboxModel);
    }

    public setColumnSort(request: LocalEvent, store: PortfolioStore): void {
        let tableTarget = request.Get<MyleTableTarget>("target");

        if (tableTarget === MyleTableTarget.Myle_PortfolioMyNotes_Search_ColumnSortClick) {
            let columnOrderType = request.Get<PortfolioNotesSortOrderType>("sortColumn");

            if (store.NotesInboxModel.get("OrderByColumn") && store.NotesInboxModel.get("OrderByColumn") === columnOrderType) {
                if (store.NotesInboxModel.get("OrderByDirection") === SortDirection.ASC)
                    store.NotesInboxModel = store.NotesInboxModel.set("OrderByDirection", SortDirection.DESC);
                else
                    store.NotesInboxModel = store.NotesInboxModel.set("OrderByDirection", SortDirection.ASC);
            }
            else {
                store.NotesInboxModel = store.NotesInboxModel.set("OrderByColumn", columnOrderType);
                store.NotesInboxModel = store.NotesInboxModel.set("OrderByDirection", SortDirection.ASC);
            }

            let column = request.Get<MyleTableColumn>("columnObject");
            if (column) {
                if (column.columnOptions.IsSortActive) {
                    column.ToggleSortDirection();
                }
                column.columnOptions.IsSortActive = true;
            }

            store.PortfolioActions.LoadMyNotes(store.NotesInboxModel);
        }
    }

    public onPatientClick(request: LocalEvent): void {
        let item = request.Get<NotesInboxTableItem>("item");

        window.setTimeout(() => {
            let openPatientOperation = new LocalEvent(LocalOperations.Misc.RedirectToTab);

            openPatientOperation.AddItem("value", item.Model.ContactId);
            openPatientOperation.AddItem("target", RedirectTo.Patient);
            openPatientOperation.AddItem("currentLocation", RouterHistory.location);
            openPatientOperation.AddItem("OpenEncounterId", item.Model.EncounterId);
            openPatientOperation.AddItem("encounterEdit", false);

            LocalEventDispatcher.dispatch(openPatientOperation);
        }, 100);
    }

    private async refreshClaimStatus(request: LocalEvent, store: PortfolioStore): Promise<void> {
        const providerClaimId = request.Get<string>("providerClaimId");
        const encounterId = request.Get<string>("encounterId");
        const billingProvider = request.Get<BillingProvider>("billingProvider");

        let data = store.NotesInboxModel.get("TableData").toArray();

        let row = data.firstOrDefault(x => x.Model.EncounterId === encounterId);

        if (!row)
            return;

        const index = row.Model.ExternalBillingClaims.findIndex(x => x.ProviderClaimId === providerClaimId && x.BillingProvider === billingProvider);
        if (index < 0)
            return;

        const updatedClaim = await ExternalBillingManager.getClaimAsync(encounterId, billingProvider, providerClaimId);

        row.Model.ExternalBillingClaims[index] = updatedClaim;
        row.Model.ExternalBillingClaims = row.Model.ExternalBillingClaims.filter(x => !!x);

        store.NotesInboxModel = store.NotesInboxModel.set("TableData", data.toImmutable());
        store.EmitChangeToListeners();
    }
}

export { NotesInboxHandler };
